// Ported behaviors from the static site's assets/site.js, scoped to <main>.
// Re-run after every SPA navigation. Excludes the design-preview widget.

import { navigate } from "svelte-routing";

/** Reveal .reveal -> .in via IntersectionObserver. */
export function initReveal(root: HTMLElement): void {
  const els = root.querySelectorAll<HTMLElement>(".reveal");
  if (!("IntersectionObserver" in window)) {
    els.forEach((e) => e.classList.add("in"));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add("in");
          io.unobserve(en.target);
        }
      });
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
  );
  els.forEach((e) => io.observe(e));
}

const BRAND_SKIP: Record<string, number> = {
  SCRIPT: 1,
  STYLE: 1,
  NOSCRIPT: 1,
  CODE: 1,
};

/** Wrap occurrences of "wiederdabei" in <span class="wd">, within root. */
export function highlightBrand(root: HTMLElement): void {
  function inSkip(node: Node): boolean {
    let el: Node | null = node.parentNode;
    while (el && el !== root) {
      const e = el as HTMLElement;
      if (e.classList && (e.classList.contains("wd") || e.classList.contains("brand"))) {
        return true;
      }
      if (BRAND_SKIP[e.nodeName]) return true;
      el = el.parentNode;
    }
    return false;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n: Node) {
      const v = n.nodeValue ?? "";
      if (v.toLowerCase().indexOf("wiederdabei") < 0) {
        return NodeFilter.FILTER_REJECT;
      }
      if (inSkip(n)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const targets: Text[] = [];
  let t: Node | null;
  while ((t = walker.nextNode())) targets.push(t as Text);

  targets.forEach((node) => {
    const parts = (node.nodeValue ?? "").split(/(wiederdabei)/i);
    if (parts.length < 2) return;
    const frag = document.createDocumentFragment();
    parts.forEach((p) => {
      if (p.toLowerCase() === "wiederdabei") {
        const span = document.createElement("span");
        span.className = "wd";
        span.textContent = "wiederdabei";
        frag.appendChild(span);
      } else if (p) {
        frag.appendChild(document.createTextNode(p));
      }
    });
    node.parentNode?.replaceChild(frag, node);
  });
}

/** Wrap table.md-table in a .table-wrap div for horizontal scroll. */
export function wrapTables(root: HTMLElement): void {
  root.querySelectorAll<HTMLTableElement>("table.md-table").forEach((tbl) => {
    const parent = tbl.parentNode as HTMLElement | null;
    if (parent && parent.classList && parent.classList.contains("table-wrap")) {
      return;
    }
    const w = document.createElement("div");
    w.className = "table-wrap";
    parent?.insertBefore(w, tbl);
    w.appendChild(tbl);
  });
}

/**
 * Delegated click handler intercepting internal <a href="/..."> links inside
 * root, routing them through svelte-routing's navigate() for SPA nav. External,
 * mailto, hash, target=_blank, and modified clicks fall through to the browser.
 * Returns a cleanup function.
 */
export function initLinkInterception(root: HTMLElement): () => void {
  const handler = (ev: MouseEvent) => {
    if (ev.defaultPrevented) return;
    if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
      return;
    }
    const target = ev.target as Element | null;
    const a = target?.closest("a");
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href) return;
    if (a.target && a.target !== "" && a.target !== "_self") return;
    if (a.hasAttribute("download")) return;

    // Only intercept same-origin clean paths.
    if (!href.startsWith("/")) return; // external/mailto/hash/relative -> browser

    ev.preventDefault();
    navigate(href);
  };

  root.addEventListener("click", handler);
  return () => root.removeEventListener("click", handler);
}

/** Run all content behaviors against root. Returns a cleanup function. */
export function applyBehaviors(root: HTMLElement): () => void {
  highlightBrand(root);
  wrapTables(root);
  initReveal(root);
  return initLinkInterception(root);
}
